import { DialogContent } from "@mui/material";
import { isCbhApiAxiosError } from "@src/appV2/Errors/errorUtils";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { useBookVisit } from "@src/appV2/redesign/InHome/BookingBottomSheet/useBookVisit";
import { BottomSheet, type UseModalState } from "@src/appV2/redesign/system/ui-components";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useQueryClient } from "@tanstack/react-query";
import { useHistory } from "react-router-dom";

import { SHIFT_DISCOVERY_IN_HOME_VISIT_BOOKED_MODAL_PATH } from "../../ShiftDiscovery/paths";
import { useInHomeVisitModalsDataContext } from "../../ShiftDiscovery/useInHomeVisitModalsDataContext";
import { InHomeVisitCardSkeleton } from "../Card/Skeleton";
import { buildWorkerStatisticsUrl } from "../lib/useInHomeStatistics";
import { type VisitWithCaseData } from "../types";
import { InHomeVisitConfirmBookingContent } from "./Content";

export interface ConfirmBookingBottomSheetProps {
  modalState: UseModalState;
  visit: VisitWithCaseData;
  isLoading: boolean;
}

export function ConfirmBookingBottomSheet(props: ConfirmBookingBottomSheetProps) {
  const { modalState, visit, isLoading } = props;
  const queryClient = useQueryClient();
  const {
    mutateAsync: bookVisit,
    isLoading: isBookingVisitLoading,
    error: bookVisitError,
    isError: isBookingVisitError,
  } = useBookVisit(visit.id);

  const history = useHistory();
  const { generateModalPath } = useInHomeVisitModalsDataContext();

  const worker = useDefinedWorker();

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <DialogFooter
          orientation="vertical"
          closeButtonAriaLabel="Close visit booking confirmation"
          onClose={() => {
            modalState.closeModal();
          }}
        >
          <LoadingButton
            fullWidth
            isLoading={isLoading || isBookingVisitLoading}
            disabled={isBookingVisitError}
            variant="contained"
            size="large"
            onClick={async () => {
              try {
                await bookVisit(
                  {
                    data: {
                      type: "visit",
                      attributes: { bookedWorkerId: worker.userId },
                    },
                  },
                  {
                    onSuccess: () => {
                      logEvent(APP_V2_USER_EVENTS.BOOKED_VISIT, { visitId: visit.id });
                      history.push(
                        generateModalPath(SHIFT_DISCOVERY_IN_HOME_VISIT_BOOKED_MODAL_PATH, {
                          visitId: visit.id,
                          caseId: visit.attributes.caseId,
                        })
                      );
                      void queryClient.invalidateQueries([buildWorkerStatisticsUrl(worker.userId)]);
                    },
                  }
                );
              } catch {
                logEvent(APP_V2_APP_EVENTS.BOOK_VISIT_FAILURE, { visitId: visit.id });
              }
            }}
          >
            Confirm booking
          </LoadingButton>
        </DialogFooter>
      }
    >
      <DialogContent sx={{ backgroundColor: "unset", py: 8, mt: 8 }}>
        {!isLoading && (
          <InHomeVisitConfirmBookingContent
            visit={visit}
            isError={isBookingVisitError}
            errorMessage={
              isCbhApiAxiosError(bookVisitError)
                ? bookVisitError.response.data.errors[0].detail
                : undefined
            }
          />
        )}
        {isLoading && <InHomeVisitCardSkeleton loadingLabel="Loading visit details" />}
      </DialogContent>
    </BottomSheet>
  );
}
